import { useCallback, useEffect, useMemo, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { FiCheck, FiCheckCircle, FiRefreshCw, FiArrowLeft, FiZap, FiClock } from 'react-icons/fi';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { useI18n } from '../../i18n/useI18n';
import { useAuth } from '../../contexts/AuthContext';
import {
  getMembershipTiers,
  getNowpaymentsOptions,
  createPaymentCheckout,
  getPaymentOrder,
  validateRedemptionCode,
  redeemCode,
} from '../../utils/api';

const STATUS_MAP = {
  pending: 'payment.statusPending',
  confirming: 'payment.statusConfirming',
  underpaid: 'payment.statusUnderpaid',
  confirmed: 'payment.statusConfirmed',
  settling: 'payment.statusSettling',
  settled: 'payment.statusSettled',
  expired: 'payment.statusExpired',
  failed: 'payment.statusFailed',
};

const isSuccessStatus = (status, paymentStatus) => {
  const normalized = (status || paymentStatus || '').toLowerCase();
  return ['settled', 'completed', 'confirmed'].includes(normalized);
};

const formatTimeLeft = (ms, t) => {
  if (ms === null || ms === undefined) return '--:--';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  if (minutes >= 60) {
    const hours = Math.floor(minutes / 60);
    const restMinutes = minutes % 60;
    return t('membership.timeHourMinute', { hours, minutes: restMinutes });
  }
  return `${minutes}:${String(seconds).padStart(2, '0')}`;
};

const FEATURE_KEY_MAP = {
  '市场浏览': 'membership.feature.marketBrowse',
  '交易员详情': 'membership.feature.traderDetail',
  '热门市场数据': 'membership.feature.hotMarketData',
  '牛人榜列表': 'membership.feature.leaderboard',
  '自动跟单与风控': 'membership.feature.autoCopyRisk',
  '所有 Free 功能': 'membership.feature.allFree',
  '所有 Lite 功能': 'membership.feature.allLite',
};

export function UpgradeModal({ open, onClose, currentTier = 'free', pushToast, onRequireLogin, onSuccess }) {
  const { t } = useI18n();
  const { isAuthenticated, membership, refreshMembership, refreshPoints } = useAuth();
  const [tiers, setTiers] = useState([]);
  const [loadingTiers, setLoadingTiers] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState('monthly');
  const [selectedTierName, setSelectedTierName] = useState(null);
  const [paymentOptions, setPaymentOptions] = useState([]);
  const [selectedChainId, setSelectedChainId] = useState('');
  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState('');
  const [checkoutInfo, setCheckoutInfo] = useState(null);
  const [orderInfo, setOrderInfo] = useState(null);
  const [orderStatus, setOrderStatus] = useState('pending');
  const [polling, setPolling] = useState(false);
  const [creatingOrder, setCreatingOrder] = useState(false);
  const [copiedAddress, setCopiedAddress] = useState(false);
  const [copiedUri, setCopiedUri] = useState(false);
  const [successHandled, setSuccessHandled] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [nextRefreshIn, setNextRefreshIn] = useState(null);
  const [showPayment, setShowPayment] = useState(false);

  // 兑换码相关状态
  const [showRedeemInput, setShowRedeemInput] = useState(false);
  const [redeemCodeValue, setRedeemCodeValue] = useState('');
  const [redeemValidating, setRedeemValidating] = useState(false);
  const [redeemResult, setRedeemResult] = useState(null); // validate 结果
  const [redeeming, setRedeeming] = useState(false);
  const [redeemError, setRedeemError] = useState('');

  const AUTO_REFRESH_SECONDS = 15;
  const yearlyDiscountPercent = 20;

  const effectiveTier = (currentTier || membership?.tier_name || 'free').toLowerCase();
  const effectiveTierLabel = t(`membership.${effectiveTier}`);

  const sortedTiers = useMemo(() => {
    return [...tiers].sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }, [tiers]);
  const isSingleTier = sortedTiers.length <= 1;

  const tierPriorityMap = useMemo(() => {
    const map = {};
    sortedTiers.forEach((tier) => {
      map[tier.name] = tier.priority ?? 0;
    });
    return map;
  }, [sortedTiers]);

  const selectedTier = useMemo(() => {
    return sortedTiers.find((tier) => tier.name === selectedTierName) || null;
  }, [sortedTiers, selectedTierName]);

  const selectedChain = useMemo(() => {
    return paymentOptions.find((chain) => chain.id === selectedChainId) || null;
  }, [paymentOptions, selectedChainId]);

  const selectedTokenPayCurrency = selectedChain?.tokens?.[selectedTokenSymbol];

  const loadTiers = useCallback(async () => {
    setLoadingTiers(true);
    try {
      const res = await getMembershipTiers();
      if (res?.code === 0) {
        setTiers(res.data?.tiers || []);
      } else {
        pushToast?.(res?.msg || t('membership.loadFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error?.message || t('membership.loadFailed'), 'error');
    } finally {
      setLoadingTiers(false);
    }
  }, [pushToast, t]);

  const loadPaymentOptions = useCallback(async () => {
    try {
      const res = await getNowpaymentsOptions();
      if (res?.code === 0) {
        const chains = res.data?.chains || [];
        setPaymentOptions(chains);
        const defaultChainId = res.data?.default_chain_id || chains[0]?.id || '';
        const defaultTokenSymbol = res.data?.default_token_symbol ||
          (chains[0]?.tokens ? Object.keys(chains[0].tokens)[0] : '');
        setSelectedChainId(defaultChainId);
        setSelectedTokenSymbol(defaultTokenSymbol);
      }
    } catch (error) {
      pushToast?.(error?.message || t('payment.optionsLoadFailed'), 'error');
    }
  }, [pushToast, t]);

  useEffect(() => {
    if (!open) return;
    setCheckoutInfo(null);
    setOrderInfo(null);
    setOrderStatus('pending');
    setSuccessHandled(false);
    setShowPayment(false);
    setShowRedeemInput(false);
    setRedeemCodeValue('');
    setRedeemValidating(false);
    setRedeemResult(null);
    setRedeeming(false);
    setRedeemError('');
    setBillingPeriod('monthly');
    loadTiers();
    loadPaymentOptions();
  }, [open, loadTiers, loadPaymentOptions]);

  useEffect(() => {
    if (!open || !sortedTiers.length || selectedTierName) return;
    const currentPriority = tierPriorityMap[effectiveTier] ?? -1;
    // 找到比当前等级更高的下一个套餐，如果没有则选中当前套餐
    const nextTier = sortedTiers.find((tier) => (tier.priority ?? 0) > currentPriority);
    if (nextTier) {
      setSelectedTierName(nextTier.name);
    } else {
      // 如果已经是最高等级，选中当前等级
      const currentTierObj = sortedTiers.find((tier) => tier.name === effectiveTier);
      if (currentTierObj) {
        setSelectedTierName(currentTierObj.name);
      } else {
        setSelectedTierName(sortedTiers[sortedTiers.length - 1]?.name);
      }
    }
  }, [open, sortedTiers, selectedTierName, tierPriorityMap, effectiveTier]);

  useEffect(() => {
    if (!selectedChain) return;
    const tokens = Object.keys(selectedChain.tokens || {});
    if (tokens.length === 0) return;
    if (!tokens.includes(selectedTokenSymbol)) {
      setSelectedTokenSymbol(tokens[0]);
    }
  }, [selectedChain, selectedTokenSymbol]);

  const handleCopy = async (value, setFlag) => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setFlag(true);
      setTimeout(() => setFlag(false), 1500);
    } catch (error) {
      setFlag(false);
    }
  };

  const handleShowPayment = () => {
    if (!isAuthenticated) {
      onRequireLogin?.();
      return;
    }
    if (!selectedTier) return;
    setShowPayment(true);
  };

  const handleCreateCheckout = async () => {
    if (!isAuthenticated) {
      onRequireLogin?.();
      return;
    }
    if (!selectedTier || creatingOrder) return;
    setCreatingOrder(true);
    try {
      const payload = {
        tier_name: selectedTier.name,
        period_months: billingPeriod === 'yearly' ? 12 : 1,
      };
      if (selectedTokenPayCurrency) {
        payload.pay_currency = selectedTokenPayCurrency;
      }
      const res = await createPaymentCheckout(payload);
      if (res?.code === 0) {
        setCheckoutInfo(res.data);
        setOrderStatus('pending');
      } else {
        pushToast?.(res?.msg || t('payment.createFailed'), 'error');
      }
    } catch (error) {
      pushToast?.(error?.message || t('payment.createFailed'), 'error');
    } finally {
      setCreatingOrder(false);
    }
  };

  const paymentOrderId = checkoutInfo?.payment_order_id;
  const isSuccess = isSuccessStatus(orderStatus, orderInfo?.payment_status);

  const pollOrder = useCallback(async (force = false) => {
    if (!paymentOrderId) return;
    setPolling(true);
    try {
      const res = await getPaymentOrder(paymentOrderId, { refresh: force });
      if (res?.code === 0) {
        const payload = res.data || {};
        const nextStatus = payload.display_status || payload.payment_status || payload.status || 'pending';
        setOrderInfo(payload);
        setOrderStatus(nextStatus);
        setNextRefreshIn(AUTO_REFRESH_SECONDS);
        const success = isSuccessStatus(nextStatus, payload.payment_status);
        if (success && !successHandled) {
          setSuccessHandled(true);
          await refreshMembership();
          await refreshPoints();
          try {
            window.dispatchEvent(new CustomEvent('membership-updated'));
          } catch {}
          pushToast?.(t('payment.upgradeSuccess'), 'success');
          // 支付成功后回调，用于跳转到牛人榜
          onSuccess?.();
        }
      }
    } catch (error) {
      pushToast?.(t('payment.statusRefreshFailed'), 'error');
    } finally {
      setPolling(false);
    }
  }, [paymentOrderId, refreshMembership, pushToast, t, successHandled]);

  useEffect(() => {
    if (!paymentOrderId) return;
    pollOrder();
    const timer = window.setInterval(() => pollOrder(), AUTO_REFRESH_SECONDS * 1000);
    return () => window.clearInterval(timer);
  }, [paymentOrderId, pollOrder]);

  useEffect(() => {
    if (!paymentOrderId || isSuccess) {
      setNextRefreshIn(null);
      return;
    }
    setNextRefreshIn(AUTO_REFRESH_SECONDS);
    const countdown = window.setInterval(() => {
      setNextRefreshIn((prev) => {
        if (prev === null) return AUTO_REFRESH_SECONDS;
        return prev > 0 ? prev - 1 : AUTO_REFRESH_SECONDS;
      });
    }, 1000);
    return () => window.clearInterval(countdown);
  }, [paymentOrderId, isSuccess]);

  useEffect(() => {
    const expiresRaw = orderInfo?.expires_at || checkoutInfo?.payment?.expires_at || checkoutInfo?.nowpayments?.expires_at;
    if (!expiresRaw) {
      setTimeLeft(null);
      return;
    }
    const expiresAt = new Date(expiresRaw);
    if (Number.isNaN(expiresAt.getTime())) {
      setTimeLeft(null);
      return;
    }
    const tick = () => {
      const diff = expiresAt.getTime() - Date.now();
      setTimeLeft(diff > 0 ? diff : 0);
    };
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [orderInfo?.expires_at, checkoutInfo?.payment?.expires_at, checkoutInfo?.nowpayments?.expires_at]);

  const handleBackToPlans = () => {
    setCheckoutInfo(null);
    setOrderInfo(null);
    setOrderStatus('pending');
    setSuccessHandled(false);
    setShowPayment(false);
  };

  // ========== 兑换码处理 ==========

  const handleValidateRedeemCode = async () => {
    if (!redeemCodeValue.trim()) return;
    setRedeemValidating(true);
    setRedeemError('');
    setRedeemResult(null);
    try {
      const res = await validateRedemptionCode(redeemCodeValue.trim());
      if (res?.code === 0 && res.data?.valid) {
        setRedeemResult(res.data);
      } else {
        setRedeemError(res?.msg || t('redemption.invalidCode'));
      }
    } catch (error) {
      setRedeemError(error?.message || t('redemption.validateFailed'));
    } finally {
      setRedeemValidating(false);
    }
  };

  const handleRedeem = async () => {
    if (!redeemCodeValue.trim()) return;
    setRedeeming(true);
    setRedeemError('');
    try {
      const res = await redeemCode(redeemCodeValue.trim());
      if (res?.code === 0 && res.data?.success) {
        pushToast?.(t('redemption.success'), 'success');
        await refreshMembership?.();
        await refreshPoints?.();
        window.dispatchEvent(new Event('membership-updated'));
        onSuccess?.();
        onClose?.();
      } else {
        setRedeemError(res?.msg || t('redemption.redeemFailed'));
      }
    } catch (error) {
      setRedeemError(error?.message || t('redemption.redeemFailed'));
    } finally {
      setRedeeming(false);
    }
  };

  const normalizedStatus = (orderStatus || '').toLowerCase();
  const uiStatus = isSuccess || ['settling', 'settled', 'confirmed'].includes(normalizedStatus)
    ? 'confirmed'
    : normalizedStatus;
  const statusLabel = t(STATUS_MAP[uiStatus] || STATUS_MAP.pending);

  const amountValue = checkoutInfo?.amount || orderInfo?.expected_amount || '--';
  const chainLabel = (checkoutInfo?.payment?.chain || checkoutInfo?.nowpayments?.chain || orderInfo?.chain || '').toUpperCase();
  const tokenLabel = (checkoutInfo?.payment?.token || checkoutInfo?.nowpayments?.token || orderInfo?.token || '').toUpperCase();
  const payAddress = checkoutInfo?.payment?.pay_address || checkoutInfo?.nowpayments?.pay_address || orderInfo?.pay_address || '';
  const payUri = checkoutInfo?.payment?.pay_uri || checkoutInfo?.nowpayments?.invoice_url || orderInfo?.pay_uri || '';
  const yearlyDiscountLabel = t('membership.discountTag', { percent: yearlyDiscountPercent });
  const resolveFeatureLabel = (feature) => {
    if (!feature) return '';
    const key = FEATURE_KEY_MAP[String(feature).trim()];
    return key ? t(key) : feature;
  };

  const getTierGradient = (tierName, isSelected) => {
    const baseClasses = isSelected ? 'ring-2 ring-offset-2 ring-offset-slate-900' : '';
    switch (tierName) {
      case 'free':
        return `${baseClasses} ${isSelected ? 'ring-slate-500 bg-gradient-to-br from-slate-800/60 to-slate-900/60 border-slate-600/50' : 'bg-slate-900/40 border-slate-700/40 hover:border-slate-600/50'}`;
      case 'lite':
        return `${baseClasses} ${isSelected ? 'ring-sky-500 bg-gradient-to-br from-sky-500/10 to-indigo-500/10 border-sky-500/40 shadow-[0_0_30px_rgba(14,165,233,0.15)]' : 'bg-slate-900/40 border-slate-700/40 hover:border-sky-500/30'}`;
      case 'pro':
        return `${baseClasses} ${isSelected ? 'ring-amber-500 bg-gradient-to-br from-amber-500/10 to-orange-500/10 border-amber-500/40 shadow-[0_0_30px_rgba(245,158,11,0.15)]' : 'bg-slate-900/40 border-slate-700/40 hover:border-amber-500/30'}`;
      default:
        return `${baseClasses} ${isSelected ? 'ring-indigo-500 bg-gradient-to-br from-indigo-500/10 to-purple-500/10 border-indigo-500/40' : 'bg-slate-900/40 border-slate-700/40'}`;
    }
  };

  const getTierIcon = (tierName) => {
    switch (tierName) {
      case 'free':
        return <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center text-slate-400 text-[10px] font-bold">{t('membership.badgeFree')}</div>;
      case 'lite':
        return <div className="w-7 h-7 rounded-lg bg-sky-500/20 flex items-center justify-center text-sky-400 text-[10px] font-bold">{t('membership.badgeLite')}</div>;
      case 'pro':
        return <div className="w-7 h-7 rounded-lg bg-amber-500/20 flex items-center justify-center text-amber-400 text-[10px] font-bold">{t('membership.badgePro')}</div>;
      default:
        return <div className="w-7 h-7 rounded-lg bg-indigo-500/20 flex items-center justify-center text-indigo-400 text-[10px] font-bold">{t('membership.badgeVip')}</div>;
    }
  };

  // 第一步：选择套餐
  const renderPlanSelection = () => (
    <div className="flex flex-col gap-4 h-full">
      {/* 当前等级和周期选择 */}
      <div className="flex items-center justify-between gap-4 bg-slate-900/40 rounded-xl p-4 border border-slate-700/40 flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-indigo-500/20 to-sky-500/20 border border-indigo-500/30 flex items-center justify-center">
            <FiZap className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <p className="text-xs text-slate-500 uppercase tracking-wider">{t('membership.currentTier')}</p>
            <div className={`
              text-base font-bold uppercase
              ${effectiveTier === 'pro' ? 'text-amber-400' : effectiveTier === 'lite' ? 'text-sky-400' : 'text-slate-300'}
            `}>
              {effectiveTierLabel}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 bg-slate-950/60 rounded-lg p-1 border border-slate-800">
          <button
            onClick={() => setBillingPeriod('monthly')}
            className={`
              px-4 py-2 rounded-md text-sm font-medium transition-all
              ${billingPeriod === 'monthly'
                ? 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'}
            `}
          >
            {t('membership.monthly')}
          </button>
          <button
            onClick={() => setBillingPeriod('yearly')}
            className={`
              px-4 py-2 rounded-md text-sm font-medium transition-all flex items-center gap-2
              ${billingPeriod === 'yearly'
                ? 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white shadow-md'
                : 'text-slate-400 hover:text-slate-200'}
            `}
          >
            {t('membership.yearly')}
            <span className="text-[10px] bg-emerald-500/20 text-emerald-400 px-1.5 py-0.5 rounded-full">
              {yearlyDiscountLabel}
            </span>
          </button>
        </div>
      </div>

      {/* 套餐卡片 - 占据主要空间 */}
      <div className={`flex-1 min-h-0 grid gap-4 ${isSingleTier ? 'md:grid-cols-1' : 'md:grid-cols-3'}`}>
        {loadingTiers ? (
          <div className="col-span-full flex items-center justify-center py-12">
            <FiRefreshCw className="w-6 h-6 text-slate-500 animate-spin" />
          </div>
        ) : (
          sortedTiers.map((tier) => {
            const isCurrent = tier.name === effectiveTier;
            const currentPriority = tierPriorityMap[effectiveTier] ?? 0;
            const tierPriority = tierPriorityMap[tier.name] ?? 0;
            const isLower = tierPriority < currentPriority;
            const isSelected = selectedTierName === tier.name;
            const price = billingPeriod === 'yearly' ? tier.price_yearly : tier.price_monthly;
            const periodLabel = billingPeriod === 'yearly' ? t('membership.year') : t('membership.month');

            return (
              <button
                key={tier.name}
                type="button"
                className={`
                  relative text-left rounded-xl border p-5 transition-all duration-300 h-full flex flex-col
                  ${isSingleTier ? 'w-full max-w-[420px] mx-auto' : ''}
                  ${getTierGradient(tier.name, isSelected)}
                  ${isLower ? 'opacity-50 cursor-not-allowed' : 'hover:scale-[1.02]'}
                `}
                onClick={() => {
                  if (isLower) return;
                  setSelectedTierName(tier.name);
                }}
              >
                {/* 选中标记 */}
                {isSelected && (
                  <div className="absolute top-3 right-3 w-5 h-5 rounded-full bg-gradient-to-r from-indigo-500 to-sky-500 flex items-center justify-center">
                    <FiCheck className="w-3 h-3 text-white" />
                  </div>
                )}

                {/* 当前方案标记 */}
                {isCurrent && (
                  <div className="absolute top-3 right-3">
                    <span className="text-[10px] uppercase px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/20">
                      {t('membership.currentPlan')}
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  {getTierIcon(tier.name)}
                  <div className="text-base font-bold text-white">{tier.display_name || tier.name}</div>
                </div>

                <div className="mb-4">
                  <span className={`
                    text-2xl font-bold
                    ${tier.name === 'pro' ? 'text-amber-400' : tier.name === 'lite' ? 'text-sky-400' : 'text-slate-300'}
                  `}>
                    ${Number(price).toFixed(2)}
                  </span>
                  <span className="text-sm text-slate-500 ml-1">/{periodLabel}</span>
                </div>

                <div className="flex-1 space-y-2 overflow-y-auto">
                  {(tier.features || []).map((feature, idx) => (
                    <div key={`${tier.name}-feature-${idx}`} className="flex items-start gap-2 text-sm text-slate-300">
                      <FiCheck className={`
                        w-4 h-4 mt-0.5 shrink-0
                        ${tier.name === 'pro' ? 'text-amber-400' : tier.name === 'lite' ? 'text-sky-400' : 'text-emerald-400'}
                      `} />
                      <span>{resolveFeatureLabel(feature)}</span>
                    </div>
                  ))}
                </div>
              </button>
            );
          })
        )}
      </div>

      {/* 底部升级按钮 */}
      <div className="flex flex-col items-center gap-3 pt-2 flex-shrink-0">
        <button
          onClick={handleShowPayment}
          disabled={!selectedTier || selectedTier?.name === effectiveTier || selectedTier?.name === 'free'}
          className={`
            px-10 py-3 rounded-xl text-base font-medium transition-all
            ${selectedTier?.name === effectiveTier || selectedTier?.name === 'free'
              ? 'bg-slate-800 text-slate-500 cursor-not-allowed'
              : 'bg-gradient-to-r from-indigo-500 to-sky-500 text-white hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20'
            }
          `}
        >
          {selectedTier?.name === effectiveTier
            ? t('membership.currentPlan')
            : t('membership.upgradeNow')}
        </button>

        {/* 兑换码入口 */}
        {!showRedeemInput ? (
          <button
            onClick={() => setShowRedeemInput(true)}
            className="text-xs text-slate-500 hover:text-sky-400 transition-colors underline underline-offset-2"
          >
            {t('redemption.haveCode')}
          </button>
        ) : (
          <div className="w-full max-w-md space-y-2">
            <div className="flex gap-2">
              <input
                type="text"
                value={redeemCodeValue}
                onChange={(e) => {
                  setRedeemCodeValue(e.target.value.toUpperCase());
                  setRedeemError('');
                  setRedeemResult(null);
                }}
                placeholder={t('redemption.placeholder')}
                maxLength={20}
                className="flex-1 bg-slate-950/60 border border-slate-700/50 rounded-lg px-3 py-2 text-sm text-white placeholder-slate-600 outline-none focus:border-sky-500/50 transition-colors"
              />
              {!redeemResult ? (
                <button
                  onClick={handleValidateRedeemCode}
                  disabled={!redeemCodeValue.trim() || redeemValidating}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-slate-800 text-slate-300 hover:bg-slate-700 hover:text-white disabled:opacity-40 disabled:cursor-not-allowed transition-all border border-slate-700/50"
                >
                  {redeemValidating ? '...' : t('redemption.validate')}
                </button>
              ) : (
                <button
                  onClick={handleRedeem}
                  disabled={redeeming}
                  className="px-4 py-2 rounded-lg text-sm font-medium bg-gradient-to-r from-emerald-500 to-teal-500 text-white hover:from-emerald-400 hover:to-teal-400 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  {redeeming ? '...' : t('redemption.redeem')}
                </button>
              )}
              <button
                onClick={() => {
                  setShowRedeemInput(false);
                  setRedeemCodeValue('');
                  setRedeemResult(null);
                  setRedeemError('');
                }}
                className="px-2 py-2 rounded-lg text-xs text-slate-500 hover:text-slate-300 transition-colors"
              >
                ✕
              </button>
            </div>

            {/* 验证成功预览 */}
            {redeemResult && (
              <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-xs text-emerald-300">
                <FiCheckCircle className="w-3.5 h-3.5 shrink-0" />
                <span>
                  {t('redemption.preview', {
                    tier: redeemResult.tier_name?.toUpperCase(),
                    months: redeemResult.duration_months,
                  })}
                </span>
              </div>
            )}

            {/* 错误提示 */}
            {redeemError && (
              <div className="px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-xs text-red-400">
                {redeemError}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // 第二步：支付页面
  const renderPayment = () => (
    <div className="flex flex-col gap-4">
      {/* 返回按钮 */}
      <div className="flex items-center justify-between">
        <button
          onClick={handleBackToPlans}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs text-slate-400 bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800 hover:text-slate-200 transition-all"
        >
          <FiArrowLeft className="w-3.5 h-3.5" />
          {t('membership.backToPlans')}
        </button>
      </div>

      {!checkoutInfo ? (
        <div className="grid gap-4 md:grid-cols-2">
          {/* 支付网络和代币选择 */}
          <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center">
                <FiZap className="w-3.5 h-3.5 text-sky-400" />
              </div>
              <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                {t('payment.selectNetwork')}
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-slate-500">{t('payment.network')}</label>
                <select
                  className="bg-slate-950/60 border border-slate-700/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-sky-500/50 transition-colors"
                  value={selectedChainId}
                  onChange={(e) => setSelectedChainId(e.target.value)}
                >
                  {paymentOptions.map((chain) => (
                    <option key={chain.id} value={chain.id}>
                      {chain.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex flex-col gap-1.5">
                <label className="text-xs text-slate-500">{t('payment.token')}</label>
                <select
                  className="bg-slate-950/60 border border-slate-700/50 rounded-lg px-3 py-2.5 text-sm text-white outline-none focus:border-sky-500/50 transition-colors"
                  value={selectedTokenSymbol}
                  onChange={(e) => setSelectedTokenSymbol(e.target.value)}
                >
                  {(selectedChain ? Object.keys(selectedChain.tokens || {}) : []).map((tokenSymbol) => (
                    <option key={tokenSymbol} value={tokenSymbol}>
                      {tokenSymbol}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* 订单摘要 */}
          <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4 flex flex-col justify-between">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-slate-800 flex items-center justify-center">
                  <FiCheck className="w-3.5 h-3.5 text-emerald-400" />
                </div>
                <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">
                  {t('payment.summary')}
                </div>
              </div>
              <div className="text-sm text-slate-300">
                {selectedTier && (
                  <>
                    <div className="flex items-center justify-between py-2 border-b border-slate-800">
                      <span className="text-slate-400">{selectedTier.display_name || selectedTier.name}</span>
                      <span className={`
                        text-lg font-bold
                        ${selectedTier.name === 'pro' ? 'text-amber-400' : selectedTier.name === 'lite' ? 'text-sky-400' : 'text-slate-200'}
                      `}>
                        ${Number(billingPeriod === 'yearly' ? selectedTier.price_yearly : selectedTier.price_monthly).toFixed(2)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between py-2">
                      <span className="text-slate-500">{billingPeriod === 'yearly' ? t('membership.yearly') : t('membership.monthly')}</span>
                      <span className="text-slate-400">× 1</span>
                    </div>
                  </>
                )}
              </div>
            </div>
            <button
              onClick={handleCreateCheckout}
              disabled={creatingOrder}
              className="w-full mt-4 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-indigo-500 to-sky-500 hover:from-indigo-400 hover:to-sky-400 shadow-lg shadow-indigo-500/20 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
            >
              {creatingOrder ? (
                <span className="flex items-center justify-center gap-2">
                  <FiRefreshCw className="w-4 h-4 animate-spin" />
                  {t('payment.creating')}
                </span>
              ) : (
                t('membership.upgradeNow')
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-4">
          {/* 状态标签 */}
          <div className="flex items-center justify-between">
            <div className={`
              flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium
              ${isSuccess
                ? 'bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                : 'bg-amber-500/15 text-amber-400 border border-amber-500/20'}
            `}>
              {isSuccess ? <FiCheck className="w-3.5 h-3.5" /> : <FiClock className="w-3.5 h-3.5" />}
              {statusLabel}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-[minmax(0,1.2fr)_minmax(0,0.8fr)]">
            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4 flex flex-col gap-3 min-w-0">
              {isSuccess ? (
                <div className="flex items-center gap-3 py-2">
                  <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                    <FiCheckCircle className="w-6 h-6 text-emerald-400" />
                  </div>
                  <div>
                    <div className="text-lg font-bold text-white">{t('payment.successTitle')}</div>
                    <div className="text-sm text-slate-400">{t('payment.successDesc')}</div>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-2 py-1">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center">
                    <FiClock className="w-4 h-4 text-amber-400" />
                  </div>
                  <div className="text-sm text-slate-400">{t('payment.waitingPayment')}</div>
                </div>
              )}

              <div className="space-y-1 text-sm">
                <div className="flex items-center justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-500">{t('payment.amount')}</span>
                  <span className="text-white font-semibold">{amountValue} {tokenLabel || 'USD'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-500">{t('payment.network')}</span>
                  <span className="text-white font-semibold">{chainLabel || '-'}</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-slate-800">
                  <span className="text-slate-500">{t('payment.token')}</span>
                  <span className="text-white font-semibold">{tokenLabel || '-'}</span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-slate-500">{t('payment.expiresIn')}</span>
                  <span className={timeLeft && timeLeft < 300000 ? 'text-rose-400 font-semibold' : 'text-white font-semibold'}>
                    {formatTimeLeft(timeLeft, t)}
                  </span>
                </div>
              </div>

              <div className="rounded-lg border border-slate-700/50 bg-slate-950/50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">{t('payment.payAddress')}</span>
                  <button
                    onClick={() => handleCopy(payAddress, setCopiedAddress)}
                    className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    {copiedAddress ? t('common.copied') : t('common.copy')}
                  </button>
                </div>
                <code className="block w-full text-xs text-slate-300 font-mono break-all">{payAddress || '-'}</code>
              </div>

              <div className="rounded-lg border border-slate-700/50 bg-slate-950/50 p-3">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-500 uppercase tracking-wider">{t('payment.payUri')}</span>
                  <button
                    onClick={() => handleCopy(payUri, setCopiedUri)}
                    className="text-xs text-sky-400 hover:text-sky-300 transition-colors"
                  >
                    {copiedUri ? t('common.copied') : t('common.copy')}
                  </button>
                </div>
                <code className="block w-full text-xs text-slate-300 font-mono break-all">{payUri || '-'}</code>
              </div>

              <div className="flex items-center gap-3 pt-1">
                <button
                  onClick={() => pollOrder(true)}
                  disabled={polling}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-sm text-slate-400 bg-slate-800/60 border border-slate-700/60 hover:bg-slate-800 hover:text-slate-200 transition-all disabled:opacity-50"
                >
                  <FiRefreshCw className={polling ? 'animate-spin' : ''} />
                  {t('payment.refreshStatus')}
                </button>
                {nextRefreshIn !== null && !isSuccess && (
                  <span className="text-xs text-slate-500">
                    {t('payment.autoRefresh', { seconds: nextRefreshIn })}
                  </span>
                )}
              </div>
            </div>

            <div className="rounded-xl border border-slate-700/40 bg-slate-900/40 p-4 flex items-center justify-center min-w-0">
              <div className="bg-white rounded-xl p-3 shadow-lg shadow-black/20">
                {payUri || payAddress ? (
                  <QRCodeSVG value={payUri || payAddress} size={160} includeMargin={true} />
                ) : (
                  <div className="w-[160px] h-[160px] rounded-lg bg-slate-100/80 flex items-center justify-center text-sm text-slate-500">
                    {t('payment.qrWaiting')}
                  </div>
                )}
              </div>
            </div>
          </div>

          {isSuccess && (
            <div className="flex justify-end pt-2">
              <button
                onClick={onClose}
                className="px-6 py-2.5 rounded-lg text-sm font-medium text-white bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-400 hover:to-teal-400 shadow-lg shadow-emerald-500/20 transition-all"
              >
                {t('payment.done')}
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      showHeader={false}
      maxWidthClass={isSingleTier ? 'max-w-5xl' : 'max-w-6xl'}
      className={isSingleTier ? 'h-[92vh]' : 'h-[96vh]'}
    >
      <div className="relative h-full flex flex-col">
        {/* 顶部渐变装饰 */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-indigo-500 via-sky-500 via-amber-500 to-indigo-500 rounded-t-2xl" />

        {/* 右上角关闭按钮 */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 w-9 h-9 inline-flex items-center justify-center bg-slate-900/60 border border-slate-600/60 text-slate-300 rounded-3xl cursor-pointer transition-all duration-200 hover:border-sky-500 hover:text-sky-100 z-10"
          aria-label={t('common.close')}
        >
          ×
        </button>

        {/* 标题区域 */}
        <div className="pt-5 pb-3 text-center flex-shrink-0">
          <div className="text-[10px] font-medium text-indigo-400 uppercase tracking-widest mb-1">
            {t('membership.upgradeKicker')}
          </div>
          <h2 className="text-xl font-bold text-white mb-1">
            {t('membership.upgradeTitle')}
          </h2>
          <p className="text-xs text-slate-400">
            {t('membership.subtitle')}
          </p>
        </div>

        {/* 根据状态显示不同内容 - 占据80%高度 */}
        <div className="flex-1 min-h-0 flex flex-col">
          {checkoutInfo ? renderPayment() : showPayment ? renderPayment() : renderPlanSelection()}
        </div>
      </div>
    </Modal>
  );
}
